"use client";
import Section from "../components/Section";
import Card from "../components/Card";
import Button from "../components/Button";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhone,
  faComments,
  faEarthAmericas,
  faChartLine,
  faShieldHalved,
  faBolt,
  faBrain,
  faLayerGroup,
  faScrewdriverWrench,
  faHeadset,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";

const Services = ({ onNavigate = () => {} }) => {
  const iconPill =
    "bg-blue-600 text-white rounded-2xl p-3 sm:p-4 inline-flex items-center justify-center";

  const services = [
    {
      title: "Enterprise Voice Solutions",
      description:
        "Next-generation voice infrastructure with HD quality, global reach, and intelligent routing powered by AI optimization for crystal-clear communications.",
      features: [
        "HD Voice Quality",
        "AI-Powered Routing",
        "Global Coverage",
        "Real-time Analytics",
      ],
      icon: faPhone,
      metric: "99.9% Quality",
    },
    {
      title: "Unified Messaging Platform",
      description:
        "Comprehensive messaging ecosystem supporting SMS, RCS, and enterprise messaging with guaranteed delivery and advanced security protocols.",
      features: [
        "Multi-Protocol Support",
        "Guaranteed Delivery",
        "Enterprise Security",
        "API Integration",
      ],
      icon: faComments,
      metric: "< 1s Delivery",
    },
    {
      title: "Global Network Interconnects",
      description:
        "Direct fiber connections and strategic peering arrangements ensuring optimal performance and redundancy across six continents.",
      features: [
        "Direct Fiber Links",
        "Strategic Peering",
        "Redundant Paths",
        "Edge Computing",
      ],
      icon: faEarthAmericas,
      metric: "< 5ms Latency",
    },
    {
      title: "Intelligent Network Analytics",
      description:
        "AI-driven monitoring and predictive analytics providing deep insights into network performance and proactive optimization recommendations.",
      features: [
        "Predictive Analytics",
        "Real-time Insights",
        "Custom Dashboards",
        "Automated Optimization",
      ],
      icon: faChartLine,
      metric: "24/7 Monitoring",
    },
  ];

  const enterpriseFeatures = [
    {
      title: "Quantum-Safe Security",
      description:
        "Future-proof encryption protocols protecting against quantum computing threats",
      icon: faShieldHalved,
    },
    {
      title: "Edge Computing Integration",
      description:
        "Distributed processing capabilities for ultra-low latency applications",
      icon: faBolt,
    },
    {
      title: "AI-Powered Optimization",
      description:
        "Machine learning algorithms continuously improving network performance",
      icon: faBrain,
    },
    {
      title: "Global Redundancy",
      description:
        "Multiple failover systems ensuring continuous service availability",
      icon: faLayerGroup,
    },
    {
      title: "Custom API Suite",
      description:
        "Comprehensive APIs for seamless integration with existing systems",
      icon: faScrewdriverWrench,
    },
    {
      title: "Dedicated Support",
      description: "Enterprise-grade support with dedicated account management",
      icon: faHeadset,
    },
  ];

  return (
    <div className="animate-fade-in">
      {/* HERO */}
      <Section className="pt-24 sm:pt-28 lg:pt-32 pb-16 sm:pb-20 lg:pb-24 overflow-visible bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="text-center max-w-6xl mx-auto space-y-6 sm:space-y-10 px-4 sm:px-6">
          <h1 className="break-words text-5xl md:text-7xl font-bold text-gray-900 tracking-tight leading-[1.12]">
            Our
            <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mt-3 pb-[0.22em] -mb-[0.22em] [-webkit-text-fill-color:transparent]">
              Services
            </span>
          </h1>
          <p className="text-base sm:text-lg md:text-2xl text-gray-700 leading-relaxed max-w-5xl mx-auto">
            Enterprise-grade telecommunications solutions engineered for modern
            global operations and real-world scale.
          </p>
        </div>
      </Section>

      {/* SERVICES GRID */}
      <Section className="py-24 sm:py-28 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 max-w-7xl mx-auto px-4 sm:px-6">
          {services.map(({ title, description, features, icon, metric }) => (
            <Card
              key={title}
              hover
              className="h-full p-8 sm:p-10 space-y-6 sm:space-y-8 bg-white border-2 border-slate-200 hover:border-blue-400 transition-all duration-500 hover:shadow-2xl">
              <div className="flex items-start justify-between gap-4">
                <div className={iconPill}>
                  <FontAwesomeIcon
                    icon={icon}
                    className="text-xl sm:text-2xl"
                    aria-hidden="true"
                  />
                </div>
                <div className="bg-blue-100 text-blue-700 px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl font-bold text-sm sm:text-base whitespace-nowrap tabular-nums leading-none flex-shrink-0">
                  <span className="inline-block align-baseline pb-[0.03em] -mb-[0.03em]">
                    {metric}
                  </span>
                </div>
              </div>

              <div className="space-y-4 sm:space-y-6">
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 leading-tight break-words">
                  {title}
                </h3>
                <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed">
                  {description}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {features.map((f, i) => (
                    <div key={i} className="flex items-start text-gray-700">
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="mt-1 w-5 h-5 text-blue-600 mr-3 flex-shrink-0"
                        aria-hidden="true"
                      />
                      <span className="text-sm md:text-base">{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </Section>

      {/* ENTERPRISE CAPABILITIES */}
      <Section className="py-24 sm:py-28 lg:py-32">
        <div className="text-center mb-14 sm:mb-16 lg:mb-24 space-y-6 sm:space-y-8 px-4 sm:px-6">
          <h2 className="break-words text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 tracking-tight leading-[1.15]">
            Enterprise-Grade
            <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mt-3 pb-[0.18em] -mb-[0.18em] [-webkit-text-fill-color:transparent]">
              Capabilities
            </span>
          </h2>
          <p className="text-base sm:text-lg md:text-2xl text-gray-600 max-w-5xl mx-auto leading-relaxed">
            Advanced features and controls to meet the most demanding
            requirements of global enterprises.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10 max-w-7xl mx-auto px-4 sm:px-6">
          {enterpriseFeatures.map(({ title, description, icon }) => (
            <Card
              key={title}
              className="text-center p-8 sm:p-10 space-y-6 sm:space-y-8 bg-gradient-to-br from-white to-slate-50 border-2 border-slate-200 hover:border-blue-300 transition-all duration-300">
              <div className={`${iconPill} w-16 h-16 sm:w-20 sm:h-20 mx-auto`}>
                <FontAwesomeIcon
                  icon={icon}
                  className="text-2xl sm:text-3xl"
                  aria-hidden="true"
                />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
                {title}
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm sm:text-base md:text-lg">
                {description}
              </p>
            </Card>
          ))}
        </div>
      </Section>

      {/* CTA */}
      <Section className="py-28 lg:py-32 relative overflow-visible bg-gradient-to-br from-blue-700 via-indigo-700 to-indigo-800 text-white">
        <div className="text-center space-y-8 sm:space-y-12 max-w-5xl mx-auto relative z-10 px-4 sm:px-6">
          <h2 className="break-words text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.12]">
            Ready to Elevate Your
            <span className="block text-blue-200 mt-4 pb-[0.14em] -mb-[0.14em]">
              Communications?
            </span>
          </h2>
          <p className="text-base sm:text-lg md:text-2xl text-blue-100 leading-relaxed">
            Let our team design a custom solution that scales with your business
            and meets your performance goals.
          </p>
          <div className="pt-4 sm:pt-8">
            <Button
              size="lg"
              onClick={() => onNavigate("contact")}
              className="text-lg md:text-2xl px-8 sm:px-12 py-4 sm:py-6 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-600 text-white shadow-2xl shadow-blue-900/30 ring-1 ring-white/10 focus:outline-none focus:ring-2 focus:ring-white/20 transition-all duration-300">
              Discuss Your Requirements
            </Button>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Services;
